import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ArrowRight, ArrowsClockwise, CircleNotch, Newspaper, ArrowSquareOut } from "@phosphor-icons/react";
import { LineChart, Line, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import SentimentAlertButton from "@/components/SentimentAlertButton";

/**
 * Slide-out drawer showing the full trade timeline + running cost basis for a
 * single symbol. Opens when the parent sets ``symbol`` non-null. Fetches on
 * open, caches for the session.
 *
 * When ``scrollToNews`` is truthy the drawer auto-scrolls the news strip
 * into view once headlines finish loading — used by the heatmap sparkline
 * click-through to land users directly on the sentiment section.
 */
export default function PositionDetailDrawer({ symbol, onClose, scrollToNews = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [sentimentFilter, setSentimentFilter] = useState("all"); // all | bullish | bearish
  const [biasHistory, setBiasHistory] = useState([]);
  const newsStripRef = useRef(null);
  const scrolledOnceRef = useRef(false);

  useEffect(() => {
    if (!symbol) {
      setData(null); setNews([]); setBiasHistory([]);
      scrolledOnceRef.current = false;
      return;
    }
    setLoading(true);
    setNewsLoading(true);
    api.get(`/positions/${symbol}/timeline`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    api.get(`/market/news/${symbol}?limit=5`)
      .then((r) => setNews(r.data.items || []))
      .catch(() => setNews([]))
      .finally(() => setNewsLoading(false));
    api.get(`/market/sentiment/history/${symbol}?days=14`)
      .then((r) => setBiasHistory(r.data.series || []))
      .catch(() => setBiasHistory([]));
  }, [symbol]);

  // Auto-scroll to the news strip when the caller requested it and the
  // headlines have finished loading. Only fires once per drawer open so a
  // parent state churn (re-render) doesn't yank users back down mid-read.
  useEffect(() => {
    if (!scrollToNews || !symbol || newsLoading || scrolledOnceRef.current) return;
    if (newsStripRef.current) {
      // Small timeout lets the Sheet finish its opening transition first,
      // otherwise the scroll target is off-screen inside a transformed sheet.
      const t = setTimeout(() => {
        newsStripRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        scrolledOnceRef.current = true;
      }, 220);
      return () => clearTimeout(t);
    }
  }, [scrollToNews, symbol, newsLoading]);

  const open = !!symbol;
  const timeline = data?.timeline || [];

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-zinc-950 border-l border-zinc-800 text-white overflow-y-auto"
        data-testid="position-detail-drawer"
      >
        <SheetHeader>
          <SheetTitle className="text-white mono text-lg font-bold">
            {symbol || "—"} <span className="text-zinc-500 font-normal text-sm">{data?.name}</span>
          </SheetTitle>
          <SheetDescription className="sr-only">
            Per-symbol trade timeline, cost basis, and recent news headlines for {symbol}.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 text-zinc-500 mono text-xs py-8">
            <CircleNotch size={14} className="animate-spin" /> Loading timeline…
          </div>
        )}

        {!loading && symbol && (
          <>
            {/* Cost-basis KPIs — only render when we have a trade timeline
                for this symbol. Watchlist-only symbols skip this block. */}
            {data && (
              <>
                <div className="grid grid-cols-2 gap-2 mt-4">
              <Kpi label="Open qty" value={data.open_quantity} testid="pd-open-qty" />
              <Kpi label="Avg cost" value={fmtMoney(data.current_avg_cost)} testid="pd-avg-cost" />
              <Kpi label="Current" value={fmtMoney(data.current_price)} testid="pd-current" />
              <Kpi label="Market value" value={fmtMoney(data.market_value)} testid="pd-market-value" />
              <Kpi
                label="Unrealized"
                value={fmtMoney(data.unrealized_pl, { showSign: true })}
                tone={data.unrealized_pl >= 0 ? "profit" : "loss"}
                testid="pd-unrealized"
              />
              <Kpi
                label="Realized"
                value={fmtMoney(data.realized_pl, { showSign: true })}
                tone={data.realized_pl >= 0 ? "profit" : "loss"}
                testid="pd-realized"
              />
            </div>

              <div className="mt-6 flex items-center gap-2">
                <Link
                  to={`/trade?s=${symbol}`}
                  onClick={onClose}
                  className="mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-sm bg-white text-black hover:bg-zinc-200 inline-flex items-center gap-1"
                  data-testid="pd-trade-link"
                >
                  Trade {symbol} <ArrowRight size={12} weight="bold" />
                </Link>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                  {data.num_buys} buy{data.num_buys === 1 ? "" : "s"} · {data.num_sells} sell{data.num_sells === 1 ? "" : "s"}
                </div>
              </div>
              </>
            )}

            {/* News strip renders regardless of whether the user holds the
                symbol — powers the heatmap sparkline click-through for
                watchlist-only tickers. */}
            <div className="mt-6" data-testid="drawer-news-strip" ref={newsStripRef}>
              <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Newspaper size={12} /> Headlines
                  {newsLoading && <CircleNotch size={11} className="animate-spin" />}
                </span>
                {news.length > 0 && (
                  <div className="flex items-center gap-1" data-testid="drawer-news-filter">
                    {["all", "bullish", "bearish"].map((f) => (
                      <button
                        key={f}
                        onClick={() => setSentimentFilter(f)}
                        data-testid={`drawer-news-filter-${f}`}
                        aria-pressed={sentimentFilter === f}
                        className={`px-1.5 py-0.5 rounded-sm border transition-colors text-[9px] uppercase tracking-widest ${
                          sentimentFilter === f
                            ? f === "bullish"
                              ? "bg-[#00C805]/15 text-[#00C805] border-[#00C805]/40"
                              : f === "bearish"
                              ? "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40"
                              : "bg-white text-black border-white"
                            : "border-zinc-700 text-zinc-500 hover:text-white hover:border-zinc-500"
                        }`}
                      >
                        {f === "bullish" ? "▲ Bull" : f === "bearish" ? "▼ Bear" : "All"}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {news.length > 0 && (() => {
                const b = news.filter((n) => n.sentiment === "bullish").length;
                const r = news.filter((n) => n.sentiment === "bearish").length;
                const n = news.filter((n) => !n.sentiment || n.sentiment === "neutral").length;
                const total = b + r + n || 1;
                const bp = Math.round((b / total) * 100);
                const rp = Math.round((r / total) * 100);
                const np = 100 - bp - rp;
                return (
                  <div className="mb-3" data-testid="drawer-sentiment-bias">
                    <div className="flex items-center justify-between mb-1">
                      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
                        Bias · {news.length} headlines
                      </div>
                      <SentimentAlertButton symbol={symbol} />
                    </div>
                    <div className="flex h-2 rounded-sm overflow-hidden bg-zinc-900 border border-zinc-800">
                      <div className="bg-[#00C805]" style={{ width: `${bp}%` }} data-testid="bias-bull" />
                      <div className="bg-zinc-600" style={{ width: `${np}%` }} data-testid="bias-neutral" />
                      <div className="bg-[#FF3B30]" style={{ width: `${rp}%` }} data-testid="bias-bear" />
                    </div>
                    <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1 flex items-center justify-between">
                      <span className="text-[#00C805]" data-testid="bias-bull-count">▲ {b} bull · {bp}%</span>
                      <span>· {n} neutral</span>
                      <span className="text-[#FF3B30]" data-testid="bias-bear-count">▼ {r} bear · {rp}%</span>
                    </div>
                    {biasHistory.length >= 2 && (
                      <div className="mt-2 h-10" data-testid="drawer-sentiment-trend">
                        <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest mb-0.5">
                          Bias · {biasHistory.length} snapshots
                        </div>
                        <ResponsiveContainer width="100%" height={30}>
                          <LineChart data={biasHistory}>
                            <YAxis hide domain={[0, 100]} />
                            <Tooltip
                              contentStyle={{
                                background: "#18181b", border: "1px solid #27272a",
                                borderRadius: 2, fontFamily: "JetBrains Mono", fontSize: 10,
                              }}
                              labelStyle={{ color: "#a1a1aa" }}
                              formatter={(v, name) => [`${v}%`, name === "bullish_pct" ? "Bull" : "Bear"]}
                              labelFormatter={(_, payload) => {
                                const at = payload?.[0]?.payload?.at;
                                return at ? new Date(at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit" }) : "";
                              }}
                            />
                            <Line type="monotone" dataKey="bullish_pct" stroke="#00C805" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                            <Line type="monotone" dataKey="bearish_pct" stroke="#FF3B30" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                );
              })()}
              {(() => {
                const filtered = news.filter((n) =>
                  sentimentFilter === "all" ? true : n.sentiment === sentimentFilter,
                );
                if (filtered.length > 0) return (
                  <div className="space-y-2">
                    {filtered.map((n) => (
                    <a
                      key={n.id || n.url}
                      href={n.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`drawer-news-item-${n.id || ""}`}
                      data-sentiment={n.sentiment || "neutral"}
                      className={`group block p-2.5 border rounded-sm transition-colors hover:bg-zinc-900/40 ${
                        n.sentiment === "bullish"
                          ? "border-[#00C805]/40 hover:border-[#00C805]"
                          : n.sentiment === "bearish"
                          ? "border-[#FF3B30]/40 hover:border-[#FF3B30]"
                          : "border-zinc-800 hover:border-zinc-700"
                      }`}
                    >
                      <div className="flex gap-2.5">
                        {n.thumbnail && (
                          <img
                            src={n.thumbnail}
                            alt=""
                            className="w-14 h-14 object-cover rounded-sm shrink-0"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-1.5">
                            {n.sentiment && n.sentiment !== "neutral" && (
                              <span
                                data-testid={`drawer-news-sentiment-${n.id || ""}`}
                                className={`mono text-[8px] uppercase tracking-widest px-1 py-px rounded-sm font-bold shrink-0 mt-0.5 ${
                                  n.sentiment === "bullish"
                                    ? "bg-[#00C805]/15 text-[#00C805]"
                                    : "bg-[#FF3B30]/15 text-[#FF3B30]"
                                }`}
                                title={`${n.sentiment} · ${Math.round((n.sentiment_confidence || 0) * 100)}% confidence`}
                              >
                                {n.sentiment === "bullish" ? "▲" : "▼"} {n.sentiment}
                              </span>
                            )}
                            <div className="text-xs text-white group-hover:text-[#00C805] transition-colors leading-snug line-clamp-2">
                              {n.title}
                            </div>
                          </div>
                          <div className="mono text-[10px] text-zinc-500 mt-1 flex items-center gap-2">
                            <span>{n.source}</span>
                            {n.published_at && (
                              <span>· {formatRelative(n.published_at)}</span>
                            )}
                            <ArrowSquareOut size={9} className="opacity-60" />
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
                );
                if (news.length > 0) return (
                  <div className="mono text-[11px] text-zinc-600" data-testid="drawer-news-empty-filter">
                    No {sentimentFilter} headlines. <button className="underline text-white" onClick={() => setSentimentFilter("all")}>Show all</button>
                  </div>
                );
                return (
                  !newsLoading && (
                    <div className="mono text-[11px] text-zinc-600">No recent headlines for {symbol}.</div>
                  )
                );
              })()}
            </div>

            {/* Timeline — also gated on ``data`` since watchlist-only
                symbols have no trade history to render. */}
            {data && (
              <div className="mt-6">
                <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-2">
                  Trade timeline
                </div>
                <ol className="relative border-l border-zinc-800 pl-4 space-y-4">
                  {timeline.slice().reverse().map((t) => (
                    <li key={t.id} data-testid={`pd-trade-${t.id}`} className="relative">
                      <div
                        className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-zinc-950 ${
                          t.side === "buy" ? "bg-[#00C805]" : "bg-[#FF3B30]"
                        }`}
                      />
                      <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                        {new Date(t.executed_at).toLocaleString()}
                      </div>
                      <div className="mono text-sm mt-0.5">
                        <span className={`font-bold ${t.side === "buy" ? "profit" : "loss"}`}>
                          {t.side.toUpperCase()}
                        </span>{" "}
                        <span>{t.quantity}</span> @ <span>{fmtMoney(t.price)}</span>{" "}
                        <span className="text-zinc-500">= {fmtMoney(t.total)}</span>
                      </div>
                      <div className="mono text-[10px] text-zinc-500 mt-0.5">
                        After: {t.running_qty} shares @ {fmtMoney(t.running_avg_cost)} avg
                        {t.side === "sell" && (
                          <span className={`ml-2 ${t.realized_pl >= 0 ? "profit" : "loss"}`}>
                            · realized {fmtMoney(t.realized_pl, { showSign: true })}
                          </span>
                        )}
                        {t.source === "bot" && (
                          <span className="ml-2 text-[#007AFF]">· bot</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
                {timeline.length === 0 && (
                  <div className="mono text-xs text-zinc-600 py-8 text-center">No trades on {symbol}.</div>
                )}
              </div>
            )}

            {/* Inline hint for watchlist-only symbols — the drawer still
                shows news + sentiment above, this just explains the absence
                of trade timeline / KPIs. */}
            {!data && (
              <div
                data-testid="drawer-no-history"
                className="mono text-[11px] text-zinc-600 mt-6 text-center border-t border-zinc-800 pt-4"
              >
                No trades on {symbol} yet — you're viewing this from your watchlist.{" "}
                <Link
                  to={`/trade?s=${symbol}`}
                  onClick={onClose}
                  className="underline text-white hover:text-[#00C805]"
                  data-testid="drawer-trade-link"
                >
                  Trade {symbol} <ArrowRight size={10} weight="bold" className="inline" />
                </Link>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Kpi({ label, value, tone, testid }) {
  return (
    <div className="p-3 border border-zinc-800 rounded-sm bg-zinc-900/40" data-testid={testid}>
      <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest">{label}</div>
      <div className={`mono text-base font-bold mt-1 ${tone || ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function formatRelative(iso) {
  try {
    const then = new Date(iso).getTime();
    const secs = Math.max(1, (Date.now() - then) / 1000);
    if (secs < 60) return `${Math.floor(secs)}s ago`;
    const mins = secs / 60;
    if (mins < 60) return `${Math.floor(mins)}m ago`;
    const hrs = mins / 60;
    if (hrs < 24) return `${Math.floor(hrs)}h ago`;
    const days = hrs / 24;
    if (days < 30) return `${Math.floor(days)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}
